import { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, Chip } from "@mui/material";

import BaseWrapper from "../../../../components/ui/BaseWrapper";
import BaseDataTableV2 from "../../../../components/ui/BaseDataTableV2";
import BaseLoader from "../../../../components/ui/BaseLoader";
import BaseButton from "../../../../components/ui/BaseButton";
import { renderUserCell } from "../../../../components/ui/UserProfileLink";
import BaseDropdownV2 from "../../../../components/ui/BaseDropdownV2";
import BaseConfirmModal from "../../../../components/ui/BaseConfirmModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import { showToast } from "../../../../store/toast/toastSlice";
import useAuth from "../../../../hooks/useAuth";
import {
  fetchChildClients,
  deleteClient,
  unlinkClientFromParent,
} from "../../../../services/api/client";
import { debounce } from "../../../../utils/helpers";
import LinkClientModal from "../LinkClientModal";
import {
  setClientDashboardFilters,
  resetClientDashboardFilters,
} from "../../../../store/filters/clientDashboardFilterSlice";
import ClientDashboardFilters from "../../components/ClientDashboardFilter";

const ChildClientListingTable = ({ hideTitle = false, actionsContainer = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { parentClientId } = useParams();
  const { getPermissionForModule } = useAuth();
  const { hasUpdateAccess, hasDeleteAccess, hasWriteAccess, hasReadAccess } =
    getPermissionForModule("CLIENT_CUSTOMER");

  const childClientFilters = useSelector((store) => store.clientDashboardFilters.childClients);

  const [searchInput, setSearchInput] = useState(childClientFilters.search);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [menuOptions, setMenuOptions] = useState([]);

  const queryParams = useMemo(
    () => ({
      page: childClientFilters.pagination.page + 1,
      limit: childClientFilters.pagination.pageSize,
      search: childClientFilters.search || "",
      clientId: childClientFilters.clientId?.value || "",
      branchId: childClientFilters.branchId?.value || "",
      teamIds: childClientFilters.teamIds || undefined,
      manager: childClientFilters.manager || "",
      country: childClientFilters.country || "",
      taxType: childClientFilters.taxType || "",
    }),
    [childClientFilters],
  );

  const {
    isLoading,
    data: responseData,
    refetch,
  } = useQuery({
    queryKey: ["child-clients", parentClientId, queryParams],
    queryFn: () => fetchChildClients(parentClientId, queryParams),
    enabled: !!parentClientId,
    placeholderData: keepPreviousData,
  });

  const childClients = responseData?.data?.data || [];
  const totalCount = responseData?.data?.totalCount || 0;
  const parentClient = responseData?.data?.parentClient;

  useEffect(() => {
    const options = [];
    if (hasUpdateAccess) options.push({ label: "Edit", value: "EDIT" });
    if (hasUpdateAccess) options.push({ label: "Unlink", value: "UNLINK" });
    if (hasDeleteAccess) options.push({ label: "Delete", value: "DELETE" });
    setMenuOptions(options);
  }, [hasUpdateAccess, hasDeleteAccess]);

  const debouncedSearchRef = useRef(
    debounce((value) => {
      dispatch(
        setClientDashboardFilters({
          module: "childClients",
          filterName: "search",
          data: value,
        }),
      );
    }, 500),
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearchRef.current(value);
  };

  const handleRemoveFilter = (key, id) => {
    if (key === "teamIds") {
      dispatch(
        setClientDashboardFilters({
          module: "childClients",
          filterName: "teamIds",
          data: childClientFilters.teamIds.filter((teamId) => teamId !== id),
        }),
      );
    } else {
      const emptyValue = key === "branchId" || key === "clientId" ? { value: "", label: "" } : "";
      dispatch(
        setClientDashboardFilters({
          module: "childClients",
          filterName: key,
          data: emptyValue,
        }),
      );
      dispatch(
        setClientDashboardFilters({
          module: "childClients",
          filterName: "pagination",
          data: { page: 0, pageSize: childClientFilters.pagination.pageSize },
        }),
      );
    }
  };

  const appliedFilters = useMemo(() => {
    const filters = [];
    const cf = childClientFilters;
    // The search term is already visible in the search box, so it gets no chip.
    if (cf.clientId?.value)
      filters.push({ prefix: "Customer", value: cf.clientId.label, key: "clientId" });
    if (cf.manager) filters.push({ prefix: "Contact Person", value: cf.manager, key: "manager" });
    if (cf.branchId?.value)
      filters.push({ prefix: "Tech Domain Branch", value: cf.branchId.label, key: "branchId" });
    if (cf.country) filters.push({ prefix: "Country", value: cf.country, key: "country" });
    if (cf.taxType) filters.push({ prefix: "Tax Type", value: cf.taxType, key: "taxType" });
    if (cf.teamIds?.length > 0) {
      cf.teamIds.forEach((id) => filters.push({ prefix: "Team", value: id, key: "teamIds", id }));
    }
    return filters;
  }, [childClientFilters]);

  const handlePaginationChange = (newPaginationModel) => {
    dispatch(
      setClientDashboardFilters({
        module: "childClients",
        filterName: "pagination",
        data: {
          page: newPaginationModel.page,
          pageSize: newPaginationModel.pageSize,
        },
      }),
    );
  };

  const handleItemClick = (value, row) => {
    if (value === "EDIT") navigate(`/workBoard/clients/edit/${row.id}`);
    if (value === "UNLINK") {
      setSelectedClient({ id: row.id, clientName: row.clientName });
      setIsUnlinkModalOpen(true);
    }
    if (value === "DELETE") {
      setSelectedClient({ id: row.id, clientName: row.clientName });
      setIsDeleteModalOpen(true);
    }
  };

  const handleUnlink = async () => {
    setIsUnlinking(true);
    try {
      await unlinkClientFromParent(selectedClient.id);
      setIsUnlinkModalOpen(false);
      dispatch(showToast({ message: "Customer unlinked successfully", type: "success" }));
      refetch();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to unlink entities",
          type: "error",
        }),
      );
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      await deleteClient(clientId);
      setIsDeleteModalOpen(false);
      dispatch(showToast({ message: "Customer deleted successfully", type: "success" }));
      refetch();
    } catch (error) {
      dispatch(showToast({ message: error?.response?.data?.message, type: "error" }));
    }
  };

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => (
        <p
          className={`table-font-size font-normal cursor-pointer ${
            hasReadAccess || hasUpdateAccess ? "text-primary" : "text-gray-500"
          }`}
          onClick={() => {
            if (hasUpdateAccess || hasReadAccess) navigate(`/workBoard/clients/edit/${params.id}`);
          }}
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "clientName",
      headerName: "Name",
      width: 280,
      renderCell: (params) => (
        <div className="flex items-center">
          <Avatar sx={{ width: 32, height: 32, marginRight: 1 }} src={params?.row?.clientLogo} />
          <p
            onClick={() => {
              if (hasUpdateAccess || hasReadAccess)
                navigate(`/workBoard/clients/edit/${params.id}`);
            }}
            className={`table-font-size truncate max-w-56 overflow-hidden whitespace-nowrap cursor-pointer ${
              hasUpdateAccess || hasReadAccess ? "text-primary" : "text-gray-500"
            }`}
          >
            {params.value || "-"}
          </p>
        </div>
      ),
    },
    {
      field: "branchName",
      headerName: "Tech Domain Branch",
      width: 200,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?.branch?.name || "-"}</p>
      ),
    },
    {
      field: "teamName",
      headerName: "Service Delivery Team",
      width: 200,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?.team?.name || "-"}</p>
      ),
    },
    {
      field: "secondaryTeamName",
      headerName: "Operations Team",
      width: 200,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?.secondaryTeam?.name || "-"}</p>
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 160,
      renderCell: (params) => {
        if (params.row?.isSelfSignup) {
          return (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
              Self Signup
            </span>
          );
        }
        return renderUserCell(params.row?.createdBy);
      },
    },
    {
      field: "country",
      headerName: "Country",
      width: 160,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?.address?.country || "-"}</p>
      ),
    },
    {
      field: "accountsPerson",
      headerName: "Accounts Person",
      width: 170,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">
          {params?.row?.account?.accountsPerson || "-"}
        </p>
      ),
    },
    {
      field: "paymentTerms",
      headerName: "Payment Terms",
      width: 150,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">
          {params?.row?.account?.paymentTerms || "-"}
        </p>
      ),
    },
    {
      field: "taxType",
      headerName: "Tax Type",
      width: 100,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?.account?.taxType || "-"}</p>
      ),
    },
    {
      field: "projectsCount",
      headerName: "Projects",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?._count?.projects ?? 0}</p>
      ),
    },
    {
      field: "workOrdersCount",
      headerName: "Total Work Orders",
      width: 160,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?._count?.workOrders ?? 0}</p>
      ),
    },
    {
      field: "publishedWorkOrdersCount",
      headerName: "Published",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?._count?.published ?? 0}</p>
      ),
    },
    {
      field: "assignedWorkOrdersCount",
      headerName: "Assigned",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?._count?.assigned ?? 0}</p>
      ),
    },
    {
      field: "workDoneWorkOrdersCount",
      headerName: "Work Done",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?._count?.workDone ?? 0}</p>
      ),
    },
    {
      field: "approvedWorkOrdersCount",
      headerName: "Approved",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?._count?.approved ?? 0}</p>
      ),
    },
    {
      field: "paidWorkOrdersCount",
      headerName: "Paid",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-pointer">{params?.row?._count?.paid ?? 0}</p>
      ),
    },
    (hasUpdateAccess || hasDeleteAccess) && {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      pinRight: true,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base" />}
          onItemClick={(value) => handleItemClick(value, params?.row)}
        />
      ),
    },
  ].filter(Boolean);

  const headerActions = (
    <>
      <BaseButton
        className="md:w-auto w-full"
        variant="outlined"
        color="primary"
        style={{
          paddingTop: "6px",
          paddingBottom: "6px",
          paddingLeft: "12px",
          paddingRight: "12px",
        }}
        onClick={() => setIsLinkModalOpen(true)}
      >
        Link Entity
      </BaseButton>
      <BaseButton
        className="md:w-auto w-full"
        variant="contained"
        color="primary"
        style={{
          paddingTop: "6px",
          paddingBottom: "6px",
          paddingLeft: "12px",
          paddingRight: "12px",
        }}
        onClick={() => navigate(`/workBoard/clients/${parentClientId}/create`)}
      >
        Create New Entity
      </BaseButton>
    </>
  );

  return (
    <>
      {isLoading && <BaseLoader isLoading={isLoading} />}

      {/* Page header, unless the page around us owns it (the parent customer tabs) */}
      {actionsContainer ? (
        hasWriteAccess && createPortal(headerActions, actionsContainer)
      ) : (
        <div className="border-b border-zinc-300 bg-white">
          <BaseWrapper>
            <div className="grid grid-cols-7 gap-y-3 py-[7px] items-center">
              <div className="md:col-span-2 col-span-7">
                {!hideTitle && (
                  <h3 className="font-light md:text-left text-center">
                    {parentClient ? parentClient.clientName : "Customer Dashboard"}
                  </h3>
                )}
              </div>
              {hasWriteAccess && (
                <div className="md:col-span-5 col-span-7 flex justify-end gap-3">
                  {headerActions}
                </div>
              )}
            </div>
          </BaseWrapper>
        </div>
      )}

      {/* Toolbar */}
      <BaseWrapper>
        <div
          className={`bg-white rounded-lg px-5 py-3.5 shadow-md mb-4 sticky top-10 z-10 flex justify-between items-center ${
            // embedded under tabs the tab strip already provides the top gap
            actionsContainer ? "" : "mt-4"
          }`}
        >
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
              <i className="fa-light fa-filter text-white text-sm"></i>
            </span>{" "}
            <p
              className="text-primary ml-2 cursor-pointer"
              onClick={() => setFiltersDrawerOpen(true)}
            >
              Add Filter
            </p>
          </div>
          <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
            <div className="flex gap-2">
              {appliedFilters.map((f) => (
                <div
                  key={f.key + f.value}
                  className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                >
                  <span className="text-primary">{f.prefix}:</span>&nbsp;
                  <p className="text-primary text-sm mx-1">{f.value}</p>
                  <button
                    onClick={() => handleRemoveFilter(f.key, f.id)}
                    className="ml-2 text-primary hover:text-red-500"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="w-[28%]">
            <BaseTextField
              placeholder="Search by name, email or ID"
              value={searchInput}
              onChange={handleSearchChange}
              startAdornment={<i className="fa-solid fa-magnifying-glass text-lg" />}
            />
          </div>
        </div>

        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)]"
          rowHeight={46}
          columns={columns}
          data={childClients}
          isLoading={isLoading}
          disableColumnResize={false}
          disableRowSelectionOnClick={true}
          hideFooter={false}
          pagination
          paginationMode="server"
          paginationModel={childClientFilters.pagination}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[10, 20, 50, 100]}
          rowCount={totalCount}
        />
      </BaseWrapper>

      {filtersDrawerOpen && (
        <ClientDashboardFilters
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          isLoading={isLoading}
          setFiltersChanged={setFiltersChanged}
          module="childClients"
          filterType="child"
        />
      )}

      {isUnlinkModalOpen && (
        <BaseConfirmModal
          open={isUnlinkModalOpen}
          onClose={() => setIsUnlinkModalOpen(false)}
          cancelText="Cancel"
          confirmText="Unlink"
          isLoading={isUnlinking}
          title="Unlink Entity"
          description={
            <p>
              Are you sure you want to unlink{" "}
              <span className="font-medium mx-1">"{selectedClient?.clientName}"</span>
              from this parent client?
            </p>
          }
          onConfirm={handleUnlink}
        />
      )}

      {isDeleteModalOpen && (
        <BaseConfirmModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          title="Delete Customer"
          description={
            <p>
              Are you sure you want to delete the customer{" "}
              <span className="text-red-600 font-medium mx-1 capitalize">
                "{selectedClient?.clientName}"
              </span>
              ? This action cannot be undone.
            </p>
          }
          onConfirm={() => handleDelete(selectedClient?.id)}
        />
      )}

      {isLinkModalOpen && (
        <LinkClientModal
          open={isLinkModalOpen}
          onClose={() => setIsLinkModalOpen(false)}
          parentClientId={Number(parentClientId)}
          parentClientName={parentClient?.clientName}
          onLinked={refetch}
        />
      )}
    </>
  );
};

export default ChildClientListingTable;
